import { cx } from "emotion";
import OutsideClickHandler from "react-outside-click-handler";
import parameterize from "parameterize";
import React, { useState } from "react";

import "../translations/workingHours";
import { getLanguage, t } from "../translations/i18n";
import Icon from "./Icon";
import isKAOpen from "../functions/isKAOpen";

import { useLocation } from "../hooks/useLocation";

import flagDe from "../assets/images/flag.de.png";
import flagUs from "../assets/images/flag.us.png";

const logo = require("../assets/images/logo.svg");

const phoneFormatted = "040 999 994 312";
const phoneLink = `tel:${phoneFormatted.replace(/ /g, "")}`;

// typeCenterLogoOnMobile: /mykitchen/KA025115-HJLH7Ltt8/proposal/a001r000018irCwAAI/
// typeCenterLogoWithSlogan: /einbaukuechen-vergleichen

function NavBar({ showMenu: showMenuProp, logoLink, typeCenterLogoOnMobile, typeCenterLogoWithSlogan }) {
  const showMenu = showMenuProp && getLanguage() === "de";

  if (typeCenterLogoWithSlogan) return <NavBarCenteredLogoWithSlogan />;
  if (typeCenterLogoOnMobile)
    return (
      <div className="t-flex t-container t-items-center t-py-2 md:t-py-5">
        <Logo className="t-hidden lg:t-flex" logoLink={logoLink} />
        <NavBarCenteredLogo className="lg:t-hidden" />
        <MenuRight full={showMenu} className="t-hidden lg:t-flex" />
      </div>
    );

  return (
    <div className="t-flex t-container t-items-center t-py-2 md:t-py-5">
      {showMenu && <MenuDropDown className=" md:t-hidden" />}
      <Logo logoLink={logoLink} />
      <MenuPhoneIconOnly className="t-flex md:t-hidden" />
      <MenuContactOnly className="t-hidden md:t-flex lg:t-hidden" />
      <MenuRight full={showMenu} className="t-hidden lg:t-flex" />
    </div>
  );
}

function NavBarCenteredLogoWithSlogan({ className }) {
  return (
    <div className={className + " t-container t-text-center t-py-5"}>
      <div className="t-inline-block">
        <img className="t-h-10" alt="" src={logo} />
        <hr className="t-border-primary-500 t-mt-5 t-mb-3" />
        <div className=" t-font-avenirLight t-text-xl">Dein bester Freund beim Küchenkauf</div>
      </div>
    </div>
  );
}

function NavBarCenteredLogo({ className }) {
  return (
    <div className={className + " t-container t-text-center t-py-3"}>
      <div className="t-inline-block">
        <img className="t-h-10" alt="" src={logo} />
      </div>
    </div>
  );
}

function Logo({ logoLink, className = "" }) {
  const img = <img className="t-h-5 lg:t-h-8" src={logo} />;
  return (
    <div className={className + " t-flex-grow"}>
      {logoLink && <a href={typeof logoLink === "boolean" ? "/" : logoLink}>{img}</a>}
      {!logoLink && img}
    </div>
  );
}

function MenuDropDown({ className }) {
  const [isOpen, setIsOpen] = useState(false);

  const classNameA = "t-block t-mb-4 t-uppercase t-text-sm t-text-black hover:t-text-black ";

  return (
    <OutsideClickHandler
      onOutsideClick={() => {
        setIsOpen(false);
      }}
    >
      <div className={className + " t-block t-mr-3"}>
        <div
          onClick={() => setIsOpen(prev => !prev)}
          className="t-border t-border-gray-300 t-border-solid t-px-4 t-py-2 t-cursor-pointer t-rounded-md"
        >
          Menü
        </div>
        {isOpen && (
          <div className="t-w-screen t-left-0 t-mt-2 t-border-gray-300 t-border t-border-solid t-whitespace-no-wrap t-z-50 t-absolute t-bg-white t-px-4 t-pt-8 t-pb-4 t-rounded-b-md">
            <a className={classNameA} href="/kuechenhersteller/">
              Küchenhersteller
            </a>
            <a className={classNameA} href="/kuechenstudios/">
              KÜCHENSTUDIOS
            </a>
            <a className={classNameA} href="/kuechenfronten/">
              Küchenfronten
            </a>
            <a className={classNameA} href="/kuechen/">
              Küchenbilder
            </a>
            <a className={classNameA} href="/faq/">
              HÄUFIGE FRAGEN
            </a>
            <a className={classNameA} href={phoneLink}>
              KONTAKT: {phoneFormatted}
            </a>
            <LanguagePositionInMobileMenu />
          </div>
        )}
      </div>
    </OutsideClickHandler>
  );
}

function LanguagePositionInMobileMenu() {
  const [isOpen, setIsOpen] = useState(false);

  const classNameA = "  t-mb-4 t-uppercase t-text-sm t-text-black hover:t-text-black  t-cursor-pointer t-group ";

  return (
    <div>
      <div className={classNameA + " t-block"} onClick={() => setIsOpen(prev => !prev)}>
        <ImgFlag src={flagDe} /> <span className=" group-hover:t-underline  t-inline-block t-mr-2">DE</span>
        <Icon className="t-text-primary-500" id={isOpen ? "faChevronUp" : "faChevronDown"} />
      </div>
      {isOpen && (
        <div>
          <ImgFlag src={flagUs} />{" "}
          <a href="/en/" className={classNameA}>
            ENGLISH (US)
          </a>
        </div>
      )}
    </div>
  );
}

function MenuContactOnly({ className }) {
  return (
    <div className={className}>
      <a className="t-text-black hover:t-text-black" href={phoneLink}>
        KONTAKT: {phoneFormatted}
      </a>
    </div>
  );
}

function MenuPhoneIconOnly({ className }) {
  return (
    <div className={className}>
      <a className="t-text-black hover:t-text-black" href={phoneLink}>
        <Icon
          id="phone"
          size={35}
          className={
            " t-text-black t-border-2 t-border-solid " +
            (isKAOpen() ? "t-border-primary-500 t-text-primary-500" : "t-border-gray-300 t-text-gray-300")
          }
        />
      </a>
    </div>
  );
}

function ImgFlag({ src }) {
  return <img src={src} className="t-h-4 t-mr-1" />;
}

function SelectedLang() {
  const lang = getLanguage() === "en" ? { title: "EN", flag: flagUs } : { title: "DE", flag: flagDe };

  return <Lang title={lang.title} flag={lang.flag} />;
}

function Lang({ title, flag, href }) {
  return (
    <span className="t-flex t-items-center">
      <ImgFlag src={flag} />
      {href && (
        <a className="t-text-black hover:t-text-black" href={href}>
          {title}
        </a>
      )}
      {!href && title}
    </span>
  );
}

function MenuRight({ full, className = "" }) {
  const [openPosition, setOpenPosition] = useState();

  function Position({ id, title, SubMenuComponent }) {
    const isOpen = id === openPosition;

    return (
      <div style={{ top: "2px" }} className=" t-flex t-relative  t-items-center">
        <div
          onClick={() => setOpenPosition(isOpen ? null : id)}
          style={{ top: "2px" }}
          className={cx(
            isOpen && "t-border-primary-400",
            " t-text-xs lg:t-text-sm xl:t-text-base t-uppercase t-relative t-cursor-pointer t-border-solid t-border-0 t-border-b-2 t-border-transparent hover:t-border-primary-400"
          )}
        >
          {title}
        </div>
        <Icon className=" t-ml-1 t-text-primary-400" size="0.8rem" id={isOpen ? "faChevronUp" : "faChevronDown"} />

        {isOpen && SubMenuComponent && <SubMenuComponent />}
      </div>
    );
  }

  return (
    <OutsideClickHandler
      onOutsideClick={() => {
        setOpenPosition(null);
      }}
    >
      <nav className={className + " t-flex t-space-x-4"}>
        {full && <Position id="inspiration" title="Kücheninspiration" SubMenuComponent={InspirationSubMenu} />}
        {full && <Position id="studios" title="Küchenstudios" SubMenuComponent={KitchenStudiosSubMenu} />}
        {full && <Position id="faq" title="Häufige Fragen" SubMenuComponent={FaqSubMenu} />}
        <Position id="contact" title={`Kontakt: ${phoneFormatted}`} SubMenuComponent={ContactSubMenu} />
        <Position id="lang" title={<SelectedLang />} SubMenuComponent={LanguageSubMenu} />
      </nav>
    </OutsideClickHandler>
  );
}

function SubMenu({ children }) {
  return (
    <nav
      style={{ top: "40px", left: "50%", transform: "translateX(-50%)" }}
      className=" t-z-50 t-bg-white t-py-4 t-px-8 t-absolute t-border t-border-solid t-rounded-md t-border-gray-500"
    >
      {children}
    </nav>
  );
}

function A({ children, href, className = "" }) {
  return (
    <a className={className + " t-text-black hover:t-text-black"} href={href}>
      {children}
    </a>
  );
}

function AListHeader({ className = "", ...props }) {
  return <A className={className + " t-font-avenirMedium t-mb-3 t-inline-block"} {...props} />;
}

function AInList({ ...props }) {
  return <A className="t-w-1/4 t-mb-2 t-text-sm t-font-avenirHeavy" {...props} />;
}

function AInListFullWidth({ ...props }) {
  return <A className="t-w-full t-mb-2 t-whitespace-no-wrap t-text-sm t-font-avenirHeavy" {...props} />;
}

function InspirationSubMenu() {
  return (
    <SubMenu>
      <div style={{ width: "500px" }}>
        <AListHeader href="/kuechenhersteller/">Küchenhersteller</AListHeader>

        <div className="t-flex t-w t-flex-wrap t-mb-4 ">
          <AInList href="/nobilia-kuechen/">Nobilia</AInList>
          <AInList href="/alno-kuechen/">Alno</AInList>
          <AInList href="/bauformat-kuechen/">Bauformat</AInList>
          <AInList href="/artego-kuechen/">Artego</AInList>
          <AInList href="/nolte-kuechen/">Nolte</AInList>
          <AInList href="/schueller-kuechen/">Schüller</AInList>
          <AInList href="/bulthaup-kuechen/">Bulthaup</AInList>
          <AInList href="/impuls-kuechen/">Impuls</AInList>
          <AInList href="/haecker-kuechen/">Häcker</AInList>
          <AInList href="/team7-kuechen/">Team7</AInList>
          <AInList href="/express-kuechen/">Express</AInList>
          <AInList href="/poggenpohl-kuechen/">Poggenpohl</AInList>
          <AInList href="/leicht-kuechen/">LEICHT</AInList>
          <AInList href="/burger-kuechen/">Burger</AInList>
          <AInList href="/ballerina-kuechen/">Ballerina</AInList>
          <AInList href="/eggersmann-kuechen/">Eggersmann</AInList>
        </div>

        <AListHeader href="/kuechen/">Küchenbilder</AListHeader>

        <div className="t-flex t-w t-flex-wrap ">
          <AInList href="/kuechen/kuechenzeilen/">Küchenzeilen</AInList>
          <AInList href="/kuechen/moderne-kuechen/">Moderne Küchen</AInList>
          <AInList href="/kuechen/familienkuechen/">Familienküchen</AInList>
          <AInList href="/kuechen/weisse-kuechen/">Weisse Küchen</AInList>
          <AInList href="/kuechen/inselkuechen/">Inselküchen</AInList>
          <AInList href="/kuechen/landhauskuechen/">Landhausküchen</AInList>
          <AInList href="/kuechen/luxuskuechen/">Luxusküchen</AInList>
          <AInList href="/kuechen/holzkuechen/">Holzküchen</AInList>
          <AInList href="/kuechen/l-kuechen/">L-Küchen</AInList>
          <AInList href="/kuechen/design-kuechen/">Design Küchen</AInList>
          <AInList href="/kuechen/buerokuechen/">Büroküchen</AInList>
          <AInList href="/kuechen/blaue-kuechen/">Blaue Küchen</AInList>
          <AInList href="/kuechen/u-kuechen/">U-Küchen</AInList>
          <AInList href="/kuechen/wohnkuechen/">Wohnküchen</AInList>
          <AInList href="/kuechen/singlekuechen/">Singleküchen</AInList>
          <AInList href="/kuechen/rote-kuechen/">Rote Küchen</AInList>
        </div>
      </div>
    </SubMenu>
  );
}

function KitchenStudiosSubMenu() {
  return (
    <SubMenu>
      <div style={{ width: "500px" }}>
        <AListHeader href="/kuechenstudios/">Küchenstudios</AListHeader>

        <div className="t-flex t-w t-flex-wrap t-mb-4 ">
          <AInList href="/kuechenstudios/berlin/">Berlin</AInList>
          <AInList href="/kuechenstudios/frankfurt/">Frankfurt</AInList>
          <AInList href="/kuechenstudios/duesseldorf/">Düsseldorf</AInList>
          <AInList href="/kuechenstudios/bremen/">Bremen</AInList>
          <AInList href="/kuechenstudios/hamburg/">Hamburg</AInList>
          <AInList href="/kuechenstudios/hannover/">Hannover</AInList>
          <AInList href="/kuechenstudios/dortmund/">Dortmund</AInList>
          <AInList href="/kuechenstudios/nuernberg/">Nürnberg</AInList>
          <AInList href="/kuechenstudios/muenchen/">München</AInList>
          <AInList href="/kuechenstudios/stuttgart/">Stuttgart</AInList>
          <AInList href="/kuechenstudios/essen/">Essen</AInList>
          <AInList href="/kuechenstudios/duisburg/">Duisburg</AInList>
          <AInList href="/kuechenstudios/koeln/">Köln</AInList>
          <AInList href="/kuechenstudios/leipzig/">Leipzig</AInList>
          <AInList href="/kuechenstudios/dresden/">Dresden</AInList>
          <AInList href="/kuechenstudios/bielefeld/">Bielefeld</AInList>
        </div>
      </div>
    </SubMenu>
  );
}

function FaqSubMenu() {
  const faqQuestions = [
    "Wie funktioniert KitchenAdvisor?",
    "Wieviel kostet KitchenAdvisor?",
    "Wie messe ich meinen Küchenraum aus?",
    "Wie entstehen meine Angebote?",
    "Was benötigt KitchenAdvisor von mir?",
  ];

  return (
    <SubMenu>
      <div>
        <AListHeader className=" t-inline-block t-w-full t-mb-2 t-text-center" href="/faq/">
          Alle Fragen
        </AListHeader>

        <div className="t-flex t-w t-flex-wrap t-mb-4 ">
          {faqQuestions.map((question, index) => (
            <AInListFullWidth key={question} href={`/faq/#q=${parameterize(question)}`}>
              {question}
            </AInListFullWidth>
          ))}
        </div>
      </div>
    </SubMenu>
  );
}

function LanguageSubMenu() {
  const language = getLanguage();
  const supportedPaths = [/\/console\/leads\/KA[^\/]+\/pricing\/kitchen\/0/];
  const location = useLocation();
  const path = location.pathname;

  const filteredPaths = path ? supportedPaths.filter(f => path.match(f)) : [];

  const lang =
    language === "de"
      ? { title: "English (US)", flag: flagUs, href: "/en/" }
      : { title: "Deutsch", flag: flagDe, href: "/" };

  if (filteredPaths?.[0]) {
    if (language === "en") {
      lang.href = path.replace(/^\/en/, "");
    } else {
      lang.href = path.replace(/^\//, "/en/");
    }
  }
  return (
    <SubMenu>
      <div className=" t-whitespace-no-wrap t-uppercase t-inline-block t-w-full t-mb-2 t-text-center" href="/faq/">
        <Lang title={lang.title} flag={lang.flag} href={lang.href} />
      </div>
    </SubMenu>
  );
}

function ContactSubMenu() {
  const isOpen = isKAOpen();

  return (
    <SubMenu>
      <div className="t-whitespace-no-wrap">
        <div className="t-flex t-items-center">
          <span className="t-mr-2  t-inline-block">
            <Icon id="phone" className={isOpen ? "t-bg-primary-500 t-text-white " : "t-bg-red-500 t-text-white"} />
          </span>
          <span className={isOpen ? "t-text-primary-500" : "t-text-red-500"}>
            {isOpen ? t("workingHours:open") : t("workingHours:close")}
          </span>
        </div>

        <hr className="t-mt-2 t-mb-2" />
        <div className="t-font-avenirHeavy t-text-sm">
          <p>{t("workingHours:workingHours")}:</p>
          <p>{t("workingHours:week")}</p>
          <p>{t("workingHours:weekend")}</p>
        </div>
      </div>
    </SubMenu>
  );
}

export default NavBar;
